<script setup>
// core
import {useTemplateRef} from "vue";
import {useInfiniteScroll} from "@vueuse/core";

// components
import AudioPlayer from "@/components/Recurring/AudioPlayer.vue";
import CallTypeView from "@/components/Telephony/CallTypeView.vue";
import CallNumberView from "@/components/Telephony/CallNumberView.vue";

// composables
import useCallDate from "@/components/Telephony/api/useCallDate";
import useScrollFade from "@/components/Telephony/api/useScrollFade";
import useFillHeight from "@/components/Telephony/api/useFillHeight";

// constants
import {ANSWERED} from "@/components/Telephony/constants";

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    // a page is on its way: the loader row sits at the foot
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(["loadMore"]);
const scrollComponent = useTemplateRef('scrollComponent')
const {formatDate, formatTime, recordName} = useCallDate()
const {isBottom} = useScrollFade(scrollComponent, () => props.data?.length)
const {maxHeight} = useFillHeight(scrollComponent)

useInfiniteScroll(
    scrollComponent,
    () => {
        emit("loadMore");
    },
    {
        distance: 50,
        interval: 300,
    }
);
</script>

<template>
    <table>
        <thead class="thead">
        <tr>
            <th
                v-for="(title, index) in $tm('telephony.calls_history.columns')"
                :key="index"
                class="th"
            >
                <span class="headed_text">{{ title }}</span>
            </th>
        </tr>
        </thead>

        <tbody class="compact-table" ref="scrollComponent" :style="{ maxHeight }">
        <tr v-for="(item, i) in props.data" :key="i">
            <td>
                <!-- the time under the date like .tariff__time in ConnectedNumbersTable -->
                <div class="when">
                    <span>{{ formatDate(item.datetime) }}</span>
                    <span class="when__time">{{ formatTime(item.datetime) }}</span>
                </div>
            </td>
            <td>
                <CallNumberView :number="item.number"/>
            </td>
            <td>
                <CallTypeView :item="item"/>
            </td>
            <td>
                <CallNumberView :number="item.contact"/>
            </td>
            <td>
                <template v-if="item.status === ANSWERED && item.duration">{{ item.duration }}</template>
                <span v-else class="no-data">—</span>
            </td>
            <td>
                <template v-if="item.status === ANSWERED">{{ item.extension }}</template>
                <span v-else class="no-data">—</span>
            </td>
            <td>
                <AudioPlayer v-if="item.recording_url" :src="item.recording_url" :download-name="recordName(item)"/>
                <span v-else class="no-data">—</span>
            </td>
        </tr>
        <tr class="info-row">
            <td :colspan="7">
                <slot name="information_column"/>
            </td>
        </tr>
        <tr class="scroll-fade" :class="{ 'is-bottom': isBottom || loading }" aria-hidden="true"></tr>
        </tbody>
    </table>
</template>

<style scoped lang="scss">
@use "@/styles/table.scss";
@use "@/styles/scroll-fade.scss";

td {
    overflow-wrap: anywhere;
}

.no-data {
    color: var(--border-light-color);
}

.when {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &__time {
        font-size: 0.85rem;
        color: var(--text-light-color);
    }
}

tbody tr.scroll-fade {
    @include scroll-fade.scroll-fade;
}

// holds loading / no data, not a data row
tbody tr.info-row {
    border-bottom: none;

    &:hover {
        background-color: transparent;
    }
}
</style>
